import 'react-app-polyfill/stable';
import { Typography } from '@material-ui/core';
import { mount, redirect, route } from 'navi';
import React, { Suspense } from 'react';
import ReactDOM from 'react-dom';
import { Router, View } from 'react-navi';
import App from './components/App/App';
import KiosksPage from './components/KiosksPage/KiosksPage';
import ScreenPage from './components/ScreenPage/ScreenPage';
import ScreensPage from './components/ScreensPage/ScreensPage';
import StoresPage from './components/StoresPage/StoresPage';
import './index.css';
import { KioskOrderingRepository } from './interfaces/KioskOrderingRepository';
import OnlineOrderingRepository from './interfaces/OnlineOrderingRepository';
import ShopOrderingRepository from './interfaces/ShopRepository';
import PromoCodeRepository from './interfaces/PromoCodeRepository';
import { FrontFacingDisplayRepository } from './interfaces/FrontFacingDisplayRepository';
import { ScreenRepository } from './interfaces/ScreenRepository';
import { KitchenDisplayRepository } from './interfaces/KitchenDisplayRepository';
import * as serviceWorker from './serviceWorker';
import { KiosksProvider } from './stores/KiosksStore';
import { ScreensProvider } from './stores/ScreensStore';
import { ScreenProvider } from './stores/ScreenStore';
import { StoresProvider } from './stores/StoresStore';
import { FrontFacingDisplayProvider } from './stores/FrontFacingDisplayStore';
import OnlineOrderingPage from './components/OrderingPage/OnlineOrderingPage';
import KioskOrderingPage from './components/OrderingPage/KioskOrderingPage';
import FrontFacingDisplayPage from './components/FrontFacingPage/FrontFacingDisplayPage';
import ShopPage from './components/ShopPage/ShopPage';
import { ShopProvider } from './stores/ShopStore';
import DeliveryRepository from './interfaces/DeliveryRepository';
import StationRepository from './interfaces/StationRepository';
import { KitchenDisplaysProvider } from './components/KitchenDisplayPage/KitchenDisplaysStore';
import { KitchenDisplayProvider } from './components/KitchenDisplayPage/KitchenDisplayStore';
import KitchenDisplayPage from './components/KitchenDisplayPage/KitchenDisplayPage';
import KitchenDisplaysPage from './components/KitchenDisplayPage/KitchenDisplaysPage';
import AuthRepository from './interfaces/AuthRepository';
import StatusBoardRepository from './interfaces/StatusBoardRepository';

import 'react-simple-keyboard/build/css/index.css';

import Amplify from 'aws-amplify';
import { AuthProvider } from './stores/AuthStore';
import { AppleWalletProvider } from './stores/AppleWalletStore';
import AppleWalletPage from './components/AppleWalletPage/AppleWalletPage';
import { OrderingProvider } from './stores/OrderingStore';

Amplify.configure({
  Auth: {
    userPoolId:
      process.env.REACT_APP_STAGE === 'production'
        ? 'us-west-1_65sPXsOWC'
        : 'us-west-1_q6IwXrjys',
    userPoolWebClientId:
      process.env.REACT_APP_STAGE === 'production'
        ? '1g7dv4gn4ghagkts6497j0ennp'
        : '57jb1s8g8uaicf040pi5783qh8'
  }
});

const basePath =
  process.env.REACT_APP_STAGE === 'production'
    ? 'https://api2-production.kiolyn.com'
    : 'https://api2-staging.kiolyn.com';
//:  'http://localhost:56103';
const promoCodeRepo = new PromoCodeRepository(basePath);
const deliveryRepo = new DeliveryRepository(basePath);
const kioskOrderingRepo = new KioskOrderingRepository(basePath);
const shopRepo = new ShopOrderingRepository(basePath);
const screenRepo = new ScreenRepository(basePath);
const kitchenDisplayRepo = new KitchenDisplayRepository(basePath);
const stationRepo = new StationRepository(basePath);
const statusBoardRepo = new StatusBoardRepository(basePath);
const frontFacingDisplayRepo = new FrontFacingDisplayRepository(basePath);
const authRepository = new AuthRepository(basePath);
const storesRepository = new OnlineOrderingRepository(basePath);
const onlineOrderingRepo = new OnlineOrderingRepository(basePath);

interface Context {}

const routes = mount<Context>({
  '/': redirect('/stores'),
  '/info': route({
    title: 'KIOLYN - Info',
    view: (
      <Typography variant="h6">
        Version: {process.env.REACT_APP_VERSION}
      </Typography>
    )
  }),
  '/stores': route<Context>({
    title: 'KIOLYN - Select store',
    view: () => {
      return (
        <StoresProvider storesRepository={storesRepository}>
          <StoresPage />
        </StoresProvider>
      );
    }
  }),
  '/store/:storeid': route<Context>(req => ({
    title: 'KIOLYN - Ordering',
    view: () => {
      return (
        <OnlineOrderingPage
          storeid={req.params.storeid}
          homeURL={req.params.home}
          onlineOrderingRepository={onlineOrderingRepo}
          promoCodeRepository={promoCodeRepo}
          deliveryRepository={deliveryRepo}
          authRepository={authRepository}
        />
      );
    }
  })),
  '/store/:storeid/:tableid': route<Context>(req => ({
    title: 'KIOLYN - Ordering',
    view: () => {
      return (
        <OnlineOrderingPage
          storeid={req.params.storeid}
          homeURL={req.params.home}
          tableId={req.params.tableid}
          onlineOrderingRepository={onlineOrderingRepo}
          promoCodeRepository={promoCodeRepo}
          deliveryRepository={deliveryRepo}
          authRepository={authRepository}
        />
      );
    }
  })),
  '/store/:storeid/shoporder': route(req => ({
    title: 'KIOLYN - Shop order',
    view: (
      <ShopProvider storeid={req.params.storeid} shopRepository={shopRepo}>
        <ShopPage />
      </ShopProvider>
    )
  })),
  '/store/:storeid/kiosks': route(req => ({
    title: 'KIOLYN - Select kiosk',
    view: (
      <KiosksProvider
        kiosksRepository={kioskOrderingRepo}
        storeid={req.params.storeid}
      >
        <KiosksPage />
      </KiosksProvider>
    )
  })),
  '/store/:storeid/kiosk/:kioskid': route(req => ({
    title: 'KIOLYN - Kiosk Ordering',
    view: (
      <KioskOrderingPage
        storeid={req.params.storeid}
        kioskid={req.params.kioskid}
        main={req.params.main}
        type={req.params.type}
        kioskOrderingRepository={kioskOrderingRepo}
      />
    )
  })),
  '/store/:storeid/screens': route(req => ({
    title: 'KIOLYN - Select screen',
    view: (
      <ScreensProvider
        screensRepository={screenRepo}
        storeid={req.params.storeid}
      >
        <ScreensPage />
      </ScreensProvider>
    )
  })),
  '/store/:storeid/screen/:screenid': route(req => ({
    title: 'KIOLYN - Screen',
    view: (
      <ScreenProvider
        screenRepository={screenRepo}
        stationRepository={stationRepo}
        statusBoardRepository={statusBoardRepo}
        storeid={req.params.storeid}
        screenid={req.params.screenid}
      >
        <ScreenPage />
      </ScreenProvider>
    )
  })),
  '/store/:storeid/kitchen-displays': route(req => ({
    title: 'KIOLYN - Select Kitchen Display',
    view: (
      <KitchenDisplaysProvider
        kitchenDisplaysRepository={kitchenDisplayRepo}
        storeid={req.params.storeid}
      >
        <KitchenDisplaysPage />
      </KitchenDisplaysProvider>
    )
  })),
  '/store/:storeid/kitchen-display/:deviceid': route(req => ({
    title: 'KIOLYN - Kitchen Display',
    view: (
      <KitchenDisplayProvider
        kitchenDisplayRepository={kitchenDisplayRepo}
        stationRepository={stationRepo}
        storeid={req.params.storeid}
        kitchenDisplayId={req.params.deviceid}
      >
        <KitchenDisplayPage />
      </KitchenDisplayProvider>
    )
  })),
  '/store/:storeid/ffd': route(req => ({
    title: 'KIOLYN - Front facing display',
    view: (
      <FrontFacingDisplayProvider
        storeid={req.params.storeid}
        frontFacingDisplayRepository={frontFacingDisplayRepo}
      >
        <FrontFacingDisplayPage />
      </FrontFacingDisplayProvider>
    )
  })),
  '/stores/:storeId/orders/:orderId/payments/applepay': route<Context>(req => ({
    title: 'KIOLYN - Apple Wallet',
    view: () => {
      return (
        <AppleWalletProvider
          storeId={req.params.storeId}
          orderId={req.params.orderId}
          onlineOrderingRepository={onlineOrderingRepo}
        >
          <OrderingProvider
            isIgnoreLoadStore={true}
            orderingRepository={onlineOrderingRepo as any}
          >
            <AppleWalletPage onlineOrderingRepository={onlineOrderingRepo} />
          </OrderingProvider>
        </AppleWalletProvider>
      );
    }
  }))
});

ReactDOM.render(
  <AuthProvider>
    <Router routes={routes}>
      <App>
        <Suspense fallback={null}>
          <View />
        </Suspense>
      </App>
    </Router>
  </AuthProvider>,
  document.getElementById('root')
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
